<template>
  <div>
    <div class="d-flex flex-row mb-6 align-center">
      <p class="text-2xl mb-6">Liste des demandes de génération d'article</p>
      <v-spacer></v-spacer>
      <v-switch
        v-model="autoReload"
        label="Rafraîchissement auto (30s)"
        inset
        dense
        class="mt-0"
      ></v-switch>
    </div>

    <v-expansion-panels multiple v-model="statsPanelVisible" class="mb-2">
      <v-expansion-panel>
        <v-expansion-panel-header> Statistiques </v-expansion-panel-header>
        <v-expansion-panel-content>
          <stats
            :params="currentFilters"
            :is-visible="isStatsPanelOpen"
          ></stats>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card>
      <v-card-text>
        <div class="d-flex flex-row mb-6">
          <v-row>
            <v-col cols="2">
              <date-range-filter
                @valueChanged="setDates"
                periodType="past-and-today"
                format="date"
                :range="30"
                :initialValue="
                  Array.isArray(initialDatesFromUrl)
                    ? []
                    : initialDatesFromUrl
                    ? initialDatesFromUrl.split(',')
                    : []
                "
                :frequencies="['daily']"
                :showBtnList="[]"
                :outlined="true"
                :onCreatedEmitDates="true"
                class="hide-frequency-toggle"
              ></date-range-filter
              ><!-- 'ALL', 'ROLLING_2_WEEKS' -->
            </v-col>

            <v-col cols="1">
              <v-text-field
                v-model="searchUser"
                label="Email user"
                dense
                clearable
              ></v-text-field>
            </v-col>

            <v-col cols="2">
              <v-text-field
                v-model="searchTopic"
                label="Topic / ID"
                dense
                clearable
              ></v-text-field>
            </v-col>

            <v-col cols="2" v-if="$can('read', 'ai_factory')">
              <site-filter
                siteListGetter="aiFactory/getSiteConnectors"
                itemValue="site.id"
                itemText="site.name"
                :multiple="false"
                :dependsOnSiteGroups="false"
                :dense="true"
                :outlined="false"
                :initialValue="searchSite"
                :noUrl="true"
                @valueChanged="setSearchSite"
              />
            </v-col>

            <v-col cols="1" v-if="$can('read', 'ai_factory')">
              <v-select
                v-model="searchOrigin"
                :items="originOptions"
                label="Origine"
                dense
                clearable
                item-text="text"
                item-value="value"
              ></v-select>
            </v-col>

            <v-col cols="1" v-if="$can('read', 'ai_factory')">
              <v-select
                v-model="searchStatus"
                :items="statusOptions"
                label="Statut"
                dense
                clearable
              ></v-select>
            </v-col>

            <v-col cols="2" v-if="$can('read', 'ai_factory')">
              <v-select
                v-model="searchErrorStep"
                :items="errorStepOptions"
                label="Étape en erreur"
                dense
                clearable
                item-text="text"
                item-value="value"
              ></v-select>
            </v-col>

            <v-col cols="1" v-if="$can('read', 'ai_factory')">
              <v-select
                v-model="searchPostStatus"
                :items="postStatusOptions"
                label="WP Status"
                dense
                clearable
                item-text="text"
                item-value="value"
              ></v-select>
            </v-col>

            <v-col cols="2" v-if="$can('read', 'ai_factory')">
              <v-select
                v-model="searchScheduled"
                :items="scheduledOptions"
                label="Programmés ?"
                dense
                clearable
                item-text="text"
                item-value="value"
              ></v-select>
            </v-col>
          </v-row>

          <dot-menu
            @exportExcel="exportExcel"
            :excelButton="{
              excelData: excelButton.data,
              filename: `ai-generated-posts-export.xlsx`,
              disabled: false,
            }"
          />
        </div>

        <v-data-table
          locale="fr-FR"
          :page="pagination.options.page"
          :page-count="pagesNumber"
          :headers="tableHeaders"
          :items="data.items"
          v-model="selected.items"
          :sort-by="'created'"
          :sort-desc="true"
          :options.sync="pagination.options"
          :server-items-length="data.total"
          :loading="loading"
          class="elevation-1 mt-4"
          :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
          :items-per-page="pagination.options.itemsPerPage"
          no-data-text="Pas de résultats"
          dense
        >
          <template v-slot:item.id="props">
            <v-tooltip right>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  {{ props.item.id }}
                </div>
              </template>
              <span>
                {{ props.item.user_name }}
                {{ props.item.user_email }}
                (ID:{{ props.item.user_id }})
              </span>
            </v-tooltip>
          </template>

          <template v-slot:item.site_name="props">
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div v-bind="attrs" v-on="on">
                  {{ props.item.site_name }}
                </div>
              </template>
              <span>{{ props.item.site_id }}</span>
            </v-tooltip>
          </template>

          <template v-slot:item.status="props">
            <div class="d-flex align-center">
              <!-- 1. STATUS FIRST (Always aligned) -->
              <div
                v-if="
                  props.item.status === 'processing' ||
                  props.item.status === 'error'
                "
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <div v-bind="attrs" v-on="on">
                      <v-chip
                        small
                        :color="getStatusColor(props.item.status)"
                        dark
                        label
                        class="mr-2"
                      >
                        {{ props.item.status }}
                      </v-chip>
                    </div>
                  </template>
                  <span>
                    <span v-if="props.item.status === 'processing'">
                      <span v-if="props.item.current_step">
                        {{ props.item.current_step }}
                      </span>
                      commencé à
                      {{
                        formatDateHumanReadableHoursMinutes(props.item.updated)
                      }}
                    </span>
                    <span
                      v-else-if="
                        props.item.status === 'error' && props.item.current_step
                      "
                    >
                      Erreur à l'étape: {{ props.item.current_step }}
                    </span>
                  </span>
                </v-tooltip>
              </div>
              <div v-else class="d-flex align-center">
                <v-chip
                  small
                  :color="getStatusColor(props.item.status)"
                  dark
                  label
                  class="mr-2"
                >
                  {{ props.item.status }}
                </v-chip>
                <v-tooltip v-if="props.item.warning" top max-width="400">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small color="warning" v-bind="attrs" v-on="on">
                      {{ icons.mdiAlert }}
                    </v-icon>
                  </template>
                  <span>{{ getWarningLabel(props.item.warning) }}</span>
                </v-tooltip>
              </div>

              <!-- 2. RETRY COUNT SECOND -->
              <v-tooltip v-if="props.item.retry_count > 0" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    v-bind="attrs"
                    v-on="on"
                    small
                    label
                    class="mr-2 retry-chip"
                  >
                    <v-icon x-small class="mr-1" color="white">{{
                      icons.mdiRestart
                    }}</v-icon>
                    {{ props.item.retry_count }} relance{{
                      props.item.retry_count > 1 ? "s" : ""
                    }}
                  </v-chip>
                </template>
                <span
                  >{{ props.item.retry_count }} relance(s) automatique(s)
                  effectuée(s)</span
                >
              </v-tooltip>
            </div>
          </template>

          <template v-slot:item.post_id="props">
            <div>
              <span v-if="props.item.post_id">
                <a
                  v-if="getAdminPostUrl(props.item)"
                  target="_blank"
                  :href="getAdminPostUrl(props.item)"
                  >{{ props.item.post_id }}</a
                >
                <span v-else>{{ props.item.post_id }}</span>
                &nbsp;<a
                  target="_blank"
                  :href="props.item.post_url"
                  style="text-decoration: none"
                  >↪</a
                >
              </span>
            </div>
          </template>

          <template v-slot:item.image_handler="props">
            <v-chip
              v-if="props.item.image_handler"
              x-small
              label
              dark
              :color="aifImageModeFor(props.item.image_handler).color"
              :title="props.item.image_handler"
            >
              <v-icon x-small left>{{
                aifImageModeFor(props.item.image_handler).icon
              }}</v-icon>
              {{ aifImageModeFor(props.item.image_handler).label }}
            </v-chip>
          </template>

          <template v-slot:item.created="props">
            {{ formatConditionalDate(props.item.created) }}
          </template>

          <template v-slot:item.scheduled="props">
            {{ formatConditionalDate(props.item.scheduled) }}
          </template>

          <template v-slot:item.updated="props">
            {{ formatDateHumanReadableHoursMinutes(props.item.updated) }}
          </template>

          <template v-slot:item.processed="props">
            {{ formatDateHumanReadableHoursMinutes(props.item.processed) }}
          </template>

          <template v-slot:item.published="props">
            {{ formatDateHumanReadableHoursMinutes(props.item.published) }}
          </template>

          <template v-slot:item.details="props">
            <v-btn
              class="mx-2"
              elevation="0"
              fab
              small
              @click="openDialogDetails(props.item)"
            >
              <v-icon color="primary">
                {{ icons.mdiEye }}
              </v-icon>
            </v-btn>
          </template>

          <template v-slot:item.dev="props">
            <v-btn class="mx-2" elevation="0" fab small> TODO later </v-btn>
          </template>

          <template v-slot:item.action="props">
            <div class="d-flex align-center">
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    :disabled="
                      isResetButtonDisabled(props.item) ||
                      resettingIds.has(props.item.id)
                    "
                    :loading="resettingIds.has(props.item.id)"
                    v-bind="attrs"
                    v-on="on"
                    class="mx-2"
                    elevation="0"
                    fab
                    small
                    @click="resetToPending(props.item)"
                  >
                    <v-icon color="orange darken-2">
                      {{ icons.mdiRestart }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>Relancer</span>
              </v-tooltip>

              <v-tooltip top v-if="$can('read', 'ai_factory')">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    v-bind="attrs"
                    v-on="on"
                    class="mx-2"
                    elevation="0"
                    fab
                    small
                    :to="`/ai-factory/generate-post/${props.item.id}`"
                  >
                    <v-icon>
                      {{ icons.mdiContentDuplicate }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>Recommencer une copie</span>
              </v-tooltip>

              <v-btn
                class="mx-2"
                elevation="0"
                fab
                small
                :disabled="isDeleteButtonDisabled(props.item)"
                @click="confirmDeleteItem(props.item)"
              >
                <v-icon color="secondary">
                  {{ icons.mdiTrashCan }}
                </v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>
<script>
import store from "@/store";
import { Konami, KonamiManager } from "@/utils/konami";
import axios from "@axios";
import {
  computed,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from "@vue/composition-api";

import useQueryString from "@/hooks/useQueryString";

import SiteFilter from "@/components/common/filters/SiteFilter";
import DotMenu from "@/components/common/menus/DotMenu";
import DateRangeFilter from "@/components/common/filters/DateRangeFilter.vue";
import DialogDetails from "@/components/ai-factory/post-generation-list/DialogDetails.vue";
import Stats from "@/components/ai-factory/post-generation-list/Stats.vue";
import { NOTIF_DISPLAY_DURATION, URL_PARAM_NAMES } from "@/utils/constants";
import { aifImageModeFor } from "@/utils/aifImageModes";
import { saveRmraView } from "@/utils/rmr-analytics";
import { buildPostAdminUrl } from "@/utils/siteConnectorAdminUrl";
import {
  formatConditionalDate,
  formatDateHumanReadableDayHoursMinutes,
  formatDateHumanReadableHoursMinutes,
  parseDateAsUtc,
} from "@/utils/dates";
import {
  mdiAlert,
  mdiContentDuplicate,
  mdiDelete,
  mdiDotsVertical,
  mdiEye,
  mdiMinus,
  mdiPencil,
  mdiPlus,
  mdiRestart,
  mdiTrashCan,
} from "@mdi/js";

const getProcessingStartDate = (item) => {
  if (item.updated) return item.updated;
  return item.created;
};

const isResetButtonDisabled = (item) => {
  // Toujours actif en local pour dev
  if (["localhost", "127.0.0.1"].includes(window.location.hostname)) {
    return false;
  }

  // 1. Désactivé si le statut est success ou pending
  if (["success", "pending"].includes(item.status)) {
    return true;
  }

  // 2. Si processing, désactivé si démarré depuis moins d'une heure
  if (item.status === "processing") {
    const startVal = getProcessingStartDate(item);
    const startDate = parseDateAsUtc(startVal);

    // Sécurité si la date est invalide
    if (isNaN(startDate.getTime())) return true;

    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);

    // Si la date de début est plus grande (plus récente) qu'il y a 1h -> return true
    return startDate > oneHourAgo;
  }

  // Sinon (ex: status 'error', ou 'processing' depuis > 1h) -> Bouton actif
  return false;
};

const isDeleteButtonDisabled = (item) => {
  if (item.status !== "processing") return false;

  const startVal = getProcessingStartDate(item);
  const startDate = parseDateAsUtc(startVal);
  if (isNaN(startDate.getTime())) return false;

  const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000);
  return startDate > oneHourAgo;
};

// Origines de generation proposees dans le filtre, dans leur ordre d'affichage.
// Appariees par `code` et jamais par id : les meta_option sont creees en
// auto-increment, donc leurs ids different d'un environnement a l'autre (cf.
// mysql/upgrade/20260826_video_transcription_origin_metaoption.sql). Les ids
// sont resolus au chargement par loadOriginOptions.
const ORIGIN_LABELS = {
  url_batch: "Via UrlBatch",
  discover: "Via Top Discover",
  topic: "Via angle",
  sources: "Via Sources",
  KW: "Via KeyWords",
  FAP: "Full AI Process - Via Sources",
  "aif-auto-kw": "Full AI Process - Via Keywords",
  internal_sources: "Via sources internes",
  video_transcription: "Via Transcription vidéo",
};

export default {
  name: "AifTmpPostGenerationList",
  components: {
    SiteFilter,
    DotMenu,
    DateRangeFilter,
    Stats,
    DialogDetails,
  },
  setup() {
    const instance = getCurrentInstance();
    const route = instance.proxy.$route;

    const {
      addQueryStringParam,
      synchronizeFilterWithQueryString,
      getUrlValue,
    } = useQueryString();

    const showDevMode = ref(false);
    let konamiManager = null;

    const excelButton = reactive({
      data: new Blob([""], { type: "text/plain" }),
    });

    const data = reactive({ total: 0, items: [] });
    const selected = reactive({ items: [] });
    const pagination = reactive({
      options: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["created"],
        sortDesc: [true],
      },
    });

    const pagesNumber = ref(0);
    const statsPanelVisible = ref([]);

    // --- FILTRES STATES ---
    const searchTopic = ref("");
    const searchUser = ref("");
    const searchSite = ref(null);
    const searchStatus = ref(null);
    const searchErrorStep = ref(null);
    const searchPostStatus = ref(null);
    const searchOrigin = ref(null);
    const searchScheduled = ref(null);

    // CORRECTION: Initialiser à null pour "Sans limite" par défaut
    const dates = ref(null);
    const initialDatesFromUrl = ref(null);

    const loading = ref(false);
    const isInitializing = ref(false);
    const optionsUpdatedByHuman = ref(false);

    const statusOptions = ["pending", "processing", "success", "error"];

    const errorStepOptions = [
      { text: "Sources", value: "get_sources_text" },
      { text: "Analyse", value: "analyse_sources" },
      { text: "Contenu", value: "generate_post_content" },
      { text: "Extras", value: "generate_post_extra" },
      {
        text: "Maillage interne",
        value: "generate_post_internal_links",
      },
      { text: "Embed vidéo", value: "insert_video_embed" },
      { text: "Titre", value: "generate_post_title" },
      { text: "Catégorie & Tags", value: "select_category_and_tags" },
      { text: "Image", value: "get_image" },
      { text: "Upload Image", value: "upload_image" },
      { text: "Publication", value: "upload_post" },
    ];

    // WP post status values stored on AifPostGeneration.post_status. UI expose
    // the two common cases (draft / publish).
    const postStatusOptions = [
      { text: "Draft", value: "draft" },
      { text: "Publié", value: "publish" },
      { text: "Future", value: "future" },
    ];

    const originOptions = ref([]);

    const loadOriginOptions = async () => {
      try {
        const { data } = await axios.get("/meta-options/rmr_origin_page/");
        const idByCode = new Map(
          (data?.items || []).map((option) => [option.code, option.id])
        );

        originOptions.value = Object.keys(ORIGIN_LABELS)
          .filter((code) => idByCode.has(code))
          .map((code) => ({
            text: ORIGIN_LABELS[code],
            value: idByCode.get(code),
          }));
      } catch (error) {
        console.error("Erreur chargement des origines:", error);
      }
    };

    const scheduledOptions = [
      { text: "Programmés", value: "scheduled" },
      { text: "Non programmés", value: "not_scheduled" },
    ];

    const autoReload = ref(true);
    let intervalId = null;
    const dialogKey = ref(0);

    // --- INITIALISATION SYNCHRONE DEPUIS L'URL ---
    // On utilise getUrlValue (lecture seule) pour peupler l'état initial
    // sans interférer avec la logique de synchronisation des composants enfants.
    const initFromUrl = () => {
      isInitializing.value = true;

      const urlSiteId = getUrlValue({
        param: "site_id",
        is_integer: true,
      });
      if (urlSiteId) {
        searchSite.value = urlSiteId;
      }

      const urlTopic = getUrlValue({
        param: URL_PARAM_NAMES["Search"],
      });
      if (urlTopic) {
        searchTopic.value = urlTopic;
      }

      const urlStatus = getUrlValue({
        param: "status",
      });
      if (urlStatus) {
        searchStatus.value = urlStatus;
      }

      const urlErrorStep = getUrlValue({
        param: "error_step",
      });
      if (urlErrorStep) {
        searchErrorStep.value = urlErrorStep;
      }

      const urlPostStatus = getUrlValue({
        param: "post_status",
      });
      if (urlPostStatus) {
        searchPostStatus.value = urlPostStatus;
      }

      // On lit la valeur de l'URL pour l'initialValue du composant enfant
      const urlDates = getUrlValue({
        param: URL_PARAM_NAMES["DateFilter"],
        is_multiple: true,
      });

      if (urlDates && urlDates.length === 2) {
        initialDatesFromUrl.value = urlDates.join(",");
        // On initialise aussi la ref 'dates' locale pour que le premier fetch tienne compte du filtre
        dates.value = urlDates.join(",");
      } else {
        // Si pas de date dans l'url, on force [] pour dire "Pas de limite"
        initialDatesFromUrl.value = [];
        dates.value = null;
      }
    };

    initFromUrl();

    // --- HELPERS ---

    const isStatsPanelOpen = computed(() => {
      return statsPanelVisible.value.includes(0);
    });

    const setSearchSite = (val) => {
      searchSite.value = val ? parseInt(val) : null;
    };

    // CORRECTION MAJEURE : Le composant DateRangeFilter émet un TABLEAU
    // ex: ['2024-11-01', '2024-11-30'] ou []
    const setDates = (val) => {
      console.log("Dates received:", val);

      let newValue = null;
      if (Array.isArray(val) && val.length === 2) {
        newValue = `${val[0]},${val[1]}`;
      } else if (typeof val === "string" && val.includes(",")) {
        newValue = val;
      }

      dates.value = newValue;

      if (!isInitializing.value) {
        fetchData();
      }
    };

    // --- COMPUTED POUR LES STATS ET API ---

    const resettingIds = ref(new Set());

    const resetToPending = async (item) => {
      resettingIds.value = new Set([...resettingIds.value, item.id]);
      try {
        // Appel du nouvel endpoint
        await axios.post("/api/ai-factory/reset-to-pending", {
          id: item.id,
        });

        await fetchData(); // Rafraîchir la liste

        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Item ${item.id} relancé avec succès (statut pending).`,
          color: "success",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      } catch (error) {
        console.error(error);
        const errorMsg =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          `Erreur lors de la relance de l'item ${item.id}.`;
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: errorMsg,
          color: "error",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      } finally {
        const next = new Set(resettingIds.value);
        next.delete(item.id);
        resettingIds.value = next;
      }
    };

    const currentFilters = computed(() => ({
      search_topic: searchTopic.value,
      search_user: searchUser.value,
      site_id:
        searchSite.value && !isNaN(searchSite.value) ? searchSite.value : null,
      status: searchStatus.value || null,
      error_step: searchErrorStep.value || null,
      post_status: searchPostStatus.value || null,
      dates: dates.value || null,
      rmr_origin_page_id: searchOrigin.value || null,
      scheduled_filter: searchScheduled.value || null,
    }));

    const fetchData = async () => {
      if (loading.value) return;

      loading.value = true;
      try {
        const params = {
          ...currentFilters.value,
          page: pagination.options.page,
          limit: pagination.options.itemsPerPage,
        };

        if (
          pagination.options.sortBy &&
          pagination.options.sortDesc &&
          pagination.options.sortBy.length > 0 &&
          pagination.options.sortDesc.length > 0
        ) {
          params.sort_by = pagination.options.sortBy[0];
          params.sort_desc = pagination.options.sortDesc[0];
        }

        const response = await axios.get("/api/ai-factory/list", {
          params,
        });

        data.total = response.data?.total || 0;
        data.items = response.data?.items || [];
        pagesNumber.value = Math.ceil(
          data.total / pagination.options.itemsPerPage
        );
      } catch (error) {
        console.error(error);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur lors du chargement des données.`,
          color: "error",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      } finally {
        loading.value = false;
      }
    };

    const exportExcel = async () => {
      const url = "/api/ai-factory/export-generation-list";
      const queryParams = {
        ...currentFilters.value,
      };

      if (
        pagination.options.sortBy &&
        pagination.options.sortDesc &&
        pagination.options.sortBy.length > 0 &&
        pagination.options.sortDesc.length > 0
      ) {
        queryParams.sort_by = pagination.options.sortBy[0];
        queryParams.sort_desc = pagination.options.sortDesc[0];
      }

      try {
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Génération de l'export en cours...`,
          color: "info",
          timeout: NOTIF_DISPLAY_DURATION,
        });

        const { data } = await axios.get(url, {
          params: queryParams,
          responseType: "blob",
        });

        excelButton.data = data;
      } catch (error) {
        console.error("Erreur lors de l'export Excel:", error);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Erreur lors de la génération de l'export.`,
          color: "error",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      }
    };

    const openDialogDetails = async (item) => {
      dialogKey.value++;
      const currentKey = dialogKey.value;
      const title = `Détails de la demande #${item.id}`;

      await store.dispatch("app/setDialog", {
        key: currentKey,
        show: true,
        title: title,
        text: '<div class="d-flex justify-center align-center pa-10"><v-progress-circular indeterminate color="primary" size="64"></v-progress-circular></div>',
        component: null,
        width: "75%",
        minWidth: 400,
        maxWidth: 1600,
        useActionButtons: false,
      });

      try {
        const response = await axios.get("/api/ai-factory/get-item", {
          params: { id: item.id },
        });
        const fullItemData = response.data;

        await store.dispatch("app/setDialog", {
          key: currentKey,
          show: true,
          title: title,
          component: DialogDetails,
          componentProps: {
            item: item,
            payload: fullItemData,
          },
          text: null,
          width: "75%",
          minWidth: 400,
          maxWidth: 1600,
          useActionButtons: false,
        });
      } catch (error) {
        console.error("Erreur chargement détails:", error);
        await store.dispatch("app/setDialog", {
          key: currentKey,
          show: true,
          title: "Erreur",
          text: "Impossible de charger les détails de l'item.",
          component: null,
          width: 400,
          useActionButtons: true,
          callbackFunction: () => {},
        });
      }
    };

    const confirmDeleteItem = async (item) => {
      await store.dispatch("app/setDialog", {
        show: true,
        type: "confirm",
        width: 800,
        title: "Suppression",
        text: `Voulez vous vraiment supprimer cette ligne (ID:${item.id}) "${item.topic}"`,
        code: null,
        callbackFunction: () => deleteItem(item.id),
      });
    };

    const deleteItem = async (id) => {
      try {
        await axios.post("/api/ai-factory/delete", null, {
          params: { id },
        });
        await fetchData();
        await store.dispatch("app/setSnackBar", {
          show: true,
          text: `Item ${id} supprimé avec succès.`,
          color: "success",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      } catch (error) {
        console.error(error);
        await store.dispatch("app/setSnackBar", {
          show: true,
          text:
            error.response?.data?.detail ||
            `Erreur lors de la suppression de l'item ${id}.`,
          color: "error",
          timeout: NOTIF_DISPLAY_DURATION,
        });
      }
    };

    const startAutoReload = () => {
      if (intervalId) clearInterval(intervalId);
      if (autoReload.value && !document.hidden) {
        intervalId = setInterval(fetchData, 30000);
      }
    };

    onMounted(async () => {
      store.commit("appConfig/UPDATE_APP_CONTENT_WIDTH", "fullWidth");
      await Promise.all([
        fetchData(),
        loadSiteConnectors(),
        loadOriginOptions(),
      ]);
      isInitializing.value = false;
      saveRmraView(route);
      startAutoReload();
      konamiManager = new KonamiManager(instance.proxy, [
        new Konami([68, 69, 86], () => {
          showDevMode.value = true;
        }),
      ]);

      document.addEventListener("visibilitychange", startAutoReload);
    });

    onBeforeUnmount(() => {
      document.removeEventListener("visibilitychange", startAutoReload);
      if (intervalId) clearInterval(intervalId);
      if (konamiManager && typeof konamiManager.destroy === "function") {
        konamiManager.destroy();
      }
    });

    watch(
      () => searchTopic.value,
      (newValue) => {
        addQueryStringParam({
          param: URL_PARAM_NAMES["Search"],
          value: newValue,
        });
        if (!isInitializing.value) {
          fetchData();
        }
      }
    );
    watch(() => searchUser.value, fetchData);
    watch(
      () => searchSite.value,
      (newValue) => {
        addQueryStringParam({
          param: "site_id",
          value: newValue,
        });
        if (!isInitializing.value) {
          fetchData();
        }
      }
    );
    watch(
      () => searchStatus.value,
      (newValue) => {
        addQueryStringParam({
          param: "status",
          value: newValue,
        });
        if (!isInitializing.value) {
          fetchData();
        }
      }
    );
    watch(
      () => searchErrorStep.value,
      (newValue) => {
        addQueryStringParam({
          param: "error_step",
          value: newValue,
        });
        if (!isInitializing.value) {
          fetchData();
        }
      }
    );
    watch(
      () => searchPostStatus.value,
      (newValue) => {
        addQueryStringParam({
          param: "post_status",
          value: newValue,
        });
        if (!isInitializing.value) {
          fetchData();
        }
      }
    );
    watch(() => searchOrigin.value, fetchData);
    watch(() => searchScheduled.value, fetchData);

    watch(
      () => pagination.options,
      () => {
        if (!optionsUpdatedByHuman.value) {
          optionsUpdatedByHuman.value = true;
          return;
        }
        fetchData();
      },
      { deep: true }
    );
    watch(autoReload, startAutoReload);

    const siteList = computed(() => store.getters["aiFactory/getSiteList"]);

    const tableHeaders = computed(() => {
      const baseHeaders = [
        { text: "ID", value: "id" },
        { text: "Site", value: "site_name" },
        { text: "Angle", value: "topic" },
        { text: "Status", value: "status" },
        { text: "Détails", value: "details", sortable: false },
        { text: "WP Status", value: "post_status" },
        { text: "Image", value: "image_handler" },
        { text: "WP Post ID", value: "post_id" },
        { text: "Créé", value: "created" },
        { text: "Programmé", value: "scheduled" },
        //{ text: "Modifié", value: "updated" },
        { text: "Traité", value: "processed" },
        { text: "Publié", value: "published" },
        { text: "Rmr Origine", value: "rmr_origin_page_name" },
        { text: "Action", value: "action", sortable: false },
      ];
      if (showDevMode.value) {
        // add { text: "Détails", value: "details", sortable: false }, in baseHeaders after post_id
        const insertIndex = baseHeaders.findIndex((h) => h.value === "post_id");
        if (insertIndex !== -1) {
          baseHeaders.splice(insertIndex + 1, 0, {
            text: "Dev",
            value: "dev",
            sortable: false,
          });
        }
      }
      return baseHeaders;
    });

    const siteConnectorsBySiteId = computed(() => {
      const siteConnectors = store.getters["aiFactory/getSiteConnectors"] || [];

      return siteConnectors.reduce((acc, connector) => {
        const connectorSiteId = connector.site_id || connector.site?.id;
        if (connectorSiteId) {
          acc[connectorSiteId] = connector;
        }
        return acc;
      }, {});
    });

    const getStatusColor = (status) => {
      switch (status) {
        case "pending":
          return "warning";
        case "processing":
          return "info";
        case "published":
        case "success":
          return "success";
        case "error":
          return "error";
        default:
          return "grey";
      }
    };

    const getWarningLabel = (warning) => {
      if (!warning) return "";
      return warning.split(" : ")[0];
    };

    const getAdminPostUrl = (item) =>
      buildPostAdminUrl({
        postUrl: item.post_url,
        postId: item.post_id,
        siteConnector: siteConnectorsBySiteId.value[item.site_id],
      });

    const loadSiteConnectors = async () => {
      if ((store.getters["aiFactory/getSiteConnectors"] || []).length) {
        return;
      }

      await store.dispatch("aiFactory/getSiteConnectors");
    };

    return {
      aifImageModeFor,
      data,
      excelButton,
      pagination,
      searchTopic,
      searchUser,
      searchSite,
      searchStatus,
      searchErrorStep,
      searchPostStatus,
      searchOrigin,
      searchScheduled,
      dates,
      initialDatesFromUrl,
      statusOptions,
      errorStepOptions,
      postStatusOptions,
      originOptions,
      scheduledOptions,
      setSearchSite,
      setDates,
      currentFilters,
      statsPanelVisible,
      isStatsPanelOpen,
      pagesNumber,
      tableHeaders,
      selected,
      loading,
      autoReload,
      deleteItem,
      confirmDeleteItem,
      formatConditionalDate,
      formatDateHumanReadableDayHoursMinutes,
      formatDateHumanReadableHoursMinutes,
      getStatusColor,
      getWarningLabel,
      getAdminPostUrl,
      isDeleteButtonDisabled,
      isResetButtonDisabled,
      showDevMode,
      exportExcel,
      openDialogDetails,
      resetToPending,
      resettingIds,
      icons: {
        mdiAlert,
        mdiEye,
        mdiPencil,
        mdiMinus,
        mdiPlus,
        mdiDelete,
        mdiTrashCan,
        mdiDotsVertical,
        mdiContentDuplicate,
        mdiRestart,
      },
    };
  },
};
</script>

<style scoped>
.retry-chip {
  background-color: #d84315 !important;
  color: #ffffff !important;
  font-weight: bold;
}

/* Masquer le switch de fréquences dans le composant DateRangeFilter enfant */
::v-deep .hide-frequency-toggle .v-btn-toggle-frequency {
  display: none !important;
}
</style>
